import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '../config';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import SEO from '../components/SEO';
import PostCard from '../components/PostCard';
import { PeopleYouMayKnow } from '../components/PeopleYouMayKnow';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DisabilityBadge } from '../components/DisabilityBadge';
import { PendingRequests } from '../components/PendingRequests';
import { Link } from 'react-router-dom';
import { 
    Users, 
    MessageCircle, 
    Calendar, 
    Building2, 
    BookOpen, 
    ArrowRight, 
    TrendingUp,
    Bookmark, 
    Clock, 
    Flame, 
    Loader2, 
    ArrowUpCircle,
    PlusCircle,
    ChevronRight
} from 'lucide-react';

const Feeds = () => {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [forumPosts, setForumPosts] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [activeTab, setActiveTab] = useState('relevant');

    const fetchForumData = async () => {
        try {
            const [postsRes, eventsRes] = await Promise.all([
                axios.get(`${API}/forums?limit=3`),
                axios.get(`${API}/events?limit=3&upcoming=true`)
            ]);
            setForumPosts(postsRes.data);
            setUpcomingEvents(eventsRes.data);
        } catch (error) {
            console.error('Failed to fetch auxiliary data:', error);
        }
    };

    const fetchFeed = useCallback(async (newSkip = 0, append = false) => {
        if (!user) return;
        try {
            const endpoint = activeTab === 'saved' 
                ? `${API}/interactions/bookmarks` 
                : `${API}/posts?limit=10&skip=${newSkip}`;
            
            const response = await axios.get(endpoint);
            const newPosts = response.data;
            
            if (append) {
                setPosts(prev => [...prev, ...newPosts]);
            } else {
                setPosts(newPosts);
            }
            
            if (activeTab === 'saved' || newPosts.length < 10) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        } catch (error) {
            console.error('Failed to fetch feed:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [user, activeTab]);

    useEffect(() => {
        setSkip(0);
        setLoading(true);
        fetchFeed(0, false);
        fetchForumData();

        // Listen for global post creation
        const handleGlobalPost = () => {
            setSkip(0);
            fetchFeed(0, false);
        };
        window.addEventListener('post-created', handleGlobalPost);
        return () => window.removeEventListener('post-created', handleGlobalPost);
    }, [fetchFeed]);

    const handleLoadMore = () => {
        const nextSkip = skip + 10;
        setSkip(nextSkip);
        setLoadingMore(true);
        fetchFeed(nextSkip, true);
    };

    const handlePostCreated = () => {
        setSkip(0);
        fetchFeed(0, false);
    };

    return (
        <Layout>
            <SEO 
                title="Dashboard" 
                description="Your unified view of community updates, discussions, and events."
            />
            <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col xl:flex-row gap-8">
                {/* Main Content Area */}
                <div className="flex-1 min-w-0 space-y-6">
                    {/* Welcome Hero */}
                    <div className="rounded-xl p-6 overflow-hidden relative border" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                        <div className="h-24 inclusion-gradient-bg opacity-20 absolute top-0 left-0 w-full" />
                        <div className="relative flex items-start gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-inclusion-red via-inclusion-gold to-inclusion-green flex items-center justify-center flex-shrink-0 shadow-lg border-2" style={{ borderColor: 'var(--bg-card)' }}>
                                <span className="text-xl font-bold text-black">{(user?.name || 'U').charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                                <h1 className="font-lexend text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                                    Welcome back, {user?.name?.split(' ')[0]}!
                                </h1>
                                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                    Here's what's happening in your community today.
                                </p>
                                {user?.disability_categories?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {user.disability_categories.map(cat => (
                                            <DisabilityBadge key={cat} category={cat} size="small" />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b scrollbar-hide overflow-x-auto whitespace-nowrap" style={{ borderColor: 'var(--border-subtle)' }}>
                        {['relevant', 'recent', 'saved', 'discussions'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className="px-4 py-2 border-b-2 font-medium text-sm transition-colors capitalize"
                                style={{
                                    borderColor: activeTab === tab ? 'var(--text-primary)' : 'transparent',
                                    color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                                    outline: 'none',
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {activeTab !== 'discussions' ? (
                        <div className="space-y-4">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center p-12 gap-4">
                                    <Loader2 className="w-8 h-8 animate-spin text-inclusion-gold" />
                                    <p className="animate-pulse font-lexend text-sm" style={{ color: 'var(--text-muted)' }}>Gathering the latest updates...</p>
                                </div>
                            ) : (
                                <>
                                    {posts.map(post => (
                                        <PostCard key={post.id} post={post} onPostUpdated={handlePostCreated} />
                                    ))}

                                    {posts.length === 0 && (
                                        <div className="text-center py-20 border border-dashed rounded-xl" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                                            <div className="mb-4 flex justify-center">
                                                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--hover-overlay)', color: 'var(--text-muted)' }}>
                                                    <ArrowUpCircle className="w-8 h-8 opacity-40" />
                                                </div>
                                            </div>
                                            <h3 className="font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Your feed is waiting</h3>
                                            <p className="text-sm max-w-xs mx-auto" style={{ color: 'var(--text-muted)' }}>
                                                Connect with others or share your first post to start the conversation!
                                            </p>
                                        </div>
                                    )}

                                    {hasMore && (
                                        <div className="flex justify-center pt-4">
                                            <Button
                                                variant="ghost"
                                                className="btn-secondary"
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                                {loadingMore ? 'Loading...' : 'Show more updates'}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                                <h2 className="font-lexend text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Community Discussions</h2>
                                <Link to="/forums">
                                    <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" style={{ color: 'var(--text-muted)' }}>
                                        Go to Forums <ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                            <div className="p-4">
                                {forumPosts.length > 0 ? (
                                    <div className="space-y-3">
                                        {forumPosts.map((post) => (
                                            <Link key={post.id} to={`/forums/${post.id}`}>
                                                <div className="p-4 rounded-xl border transition-colors" style={{ backgroundColor: 'var(--bg-page)', borderColor: 'var(--border-subtle)' }}>
                                                    <h3 className="font-medium mb-1 line-clamp-1" style={{ color: 'var(--text-primary)' }}>{post.title}</h3>
                                                    <p className="text-sm line-clamp-2 mb-2" style={{ color: 'var(--text-muted)' }}>{post.content}</p>
                                                    <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-faint)' }}>
                                                        <span>{post.author_name}</span>
                                                        <span>{post.comments_count} comments</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
                                        <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>No discussions found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar */}
                <div className="w-full xl:w-80 flex-shrink-0 space-y-6">
                    <div className="sticky top-24 space-y-6 pb-20">

                        {/* Quick Actions */}
                        <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                            <h3 className="font-lexend uppercase tracking-wider text-[10px] font-bold mb-3" style={{ color: 'var(--text-muted)' }}>Quick Actions</h3>
                            <div className="space-y-1">
                                {[
                                    { path: '/dashboard?action=new_post', label: 'Start Post', icon: PlusCircle, color: '#FCD34D' },
                                    { path: '/forums/new', label: 'Start Discussion', icon: MessageCircle, color: '#38BDF8' },
                                    { path: '/events?action=new', label: 'Create Event', icon: Calendar, color: '#34D399' },
                                    { path: '/resources?action=new', label: 'Add Resource', icon: BookOpen, color: '#FF5C5C' }
                                ].map((link) => {
                                    const Icon = link.icon;
                                    return (
                                        <Link key={link.path} to={link.path}
                                            className="flex items-center gap-3 p-2 rounded-lg transition-colors group"
                                            style={{ color: 'var(--text-secondary)' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${link.color}20` }}>
                                                <Icon className="w-3.5 h-3.5" style={{ color: link.color }} />
                                            </div>
                                            <span className="text-xs font-medium">{link.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        <PendingRequests />
                        <PeopleYouMayKnow />

                        {/* Upcoming Events */}
                        <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-lexend uppercase tracking-wider text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Events</h3>
                                <Link to="/events" className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--text-faint)' }}>See All</Link>
                            </div>
                            {upcomingEvents.length > 0 ? (
                                <div className="space-y-3">
                                    {upcomingEvents.map((event) => (
                                        <Link key={event.id} to={`/events/${event.id}`} className="block group">
                                            <div className="flex items-start gap-3 p-2 rounded-lg -mx-2 transition-colors"
                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'}
                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <div className="w-9 h-9 rounded-lg bg-inclusion-blue/20 flex flex-col items-center justify-center flex-shrink-0">
                                                    <span className="text-[9px] text-inclusion-blue font-semibold uppercase leading-tight">
                                                        {new Date(event.start_date).toLocaleDateString('en', { month: 'short' })}
                                                    </span>
                                                    <span className="text-xs font-bold text-inclusion-blue leading-tight">
                                                        {new Date(event.start_date).getDate()}
                                                    </span>
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-xs font-medium mb-0.5 line-clamp-1 group-hover:text-inclusion-blue transition-colors" style={{ color: 'var(--text-secondary)' }}>{event.title}</h3>
                                                    <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-faint)' }}>
                                                        <Clock className="w-2.5 h-2.5 flex-shrink-0" />
                                                        <span className="truncate">{event.location || 'Virtual'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-xs py-2" style={{ color: 'var(--text-faint)' }}>No upcoming events</p>
                            )}
                        </div>

                        {/* Trending */}
                        <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                            <h3 className="font-lexend font-semibold uppercase tracking-wider text-[10px] mb-4" style={{ color: 'var(--text-muted)' }}>Trending</h3>
                            <div className="flex flex-wrap gap-2">
                                {['#Inclusion', '#Accessibility', '#MyEnAb', '#SocialGraph', '#Innovation'].map(tag => (
                                    <button key={tag}
                                        className="text-[10px] px-2 py-1 rounded-md transition-colors"
                                        style={{ backgroundColor: 'var(--hover-overlay)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
                                        onMouseEnter={e => { e.currentTarget.style.color = '#FFD700'; e.currentTarget.style.borderColor = '#FFD700'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Feeds;
